'use client'

import React, { ReactNode } from 'react';
import Link from 'next/link';
import { mainColorPalette } from '@/theme/colors';

type ButtonProps = {
  id: string;
  href?: string;
  onClick?: () => void;
  className?: string;
  ariaLabel: string;
  children?: ReactNode;
  type?: 'outlined' | 'contained' | 'text' | 'icon';
  target?: string;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  useGradient?: boolean;
};

const Button = ({
  id,
  href,
  onClick,
  className = '',
  ariaLabel,
  children,
  type,
  target,
  icon,
  iconPosition = 'left',
  useGradient = true
}: ButtonProps) => {
  const buttonId = id.replace(/\s+/g, '');
  const buttonType = type ?? 'contained';
  let content;

  const outerWrapper = useGradient
    ? 'bg-gradient-to-br from-purple-600 to-blue-500 group-hover:from-purple-600 group-hover:to-blue-500'
    : 'bg-[var(--background-color)] border border-[var(--border-color)]';

  const innerWrapper = useGradient
    ? 'bg-[var(--background-color)] text-white group-hover:bg-opacity-0'
    : 'bg-[var(--background-color)] text-white';

  const iconLeft = icon && iconPosition === 'left' && <span className="flex items-center mr-2" aria-hidden="true">{icon}</span>;
  const iconRight = icon && iconPosition === 'right' && <span className="flex items-center ml-2" aria-hidden="true">{icon}</span>;


  switch (buttonType) {
    case 'contained':
      content = (
        <span
          className={`relative inline-flex items-center justify-center p-0.5 overflow-hidden text-white font-semibold rounded-full group ${outerWrapper} ${className}`}
        >
          <span
            className={`flex items-center elative py-2 px-5 transition-all ease-in duration-75 text-white rounded-full`}
          >
            {iconLeft}
            <span className="whitespace-nowrap">{children}</span>
            {iconRight}
          </span>
        </span>
      );
      break;
    case 'outlined':
      content = (
        <span
          className={`relative inline-flex items-center justify-center p-0.5 overflow-hidden text-white font-semibold rounded-full group ${outerWrapper} ${className}`}
        >
          <span className={`flex items-center justify-between relative py-2 px-5 transition-all ease-in duration-75 ${innerWrapper} rounded-full w-[inherit]`}>
            {iconLeft}
            <span className="whitespace-nowrap">{children}</span>
            {iconRight}
          </span>
        </span>
      );
      break;
    case 'text':
      content = (
        <span
          className={`inline-flex items-center py-2 px-5 font-semibold text-[${mainColorPalette.button.text}] ${className}`}
        >
          {iconLeft}
          <span className="whitespace-nowrap">{children}</span>
          {iconRight}
        </span>
      );
      break;
    case 'icon':
      content = (
        <span
          className={`relative inline-block items-center justify-center p-0.5 overflow-hidden text-white font-semibold rounded-full group ${outerWrapper} ${className}`}
        >
          <span
            className={`relative w-10 h-10 flex items-center justify-center ${innerWrapper} rounded-full`}
          >
            {icon}
          </span>
        </span>
      );
      break;
  }

  return href ? (
    <Link
      id={buttonId}
      href={href}
      aria-label={ariaLabel}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      title={ariaLabel}
    >
      {content}
    </Link>
  ) : (
    <button
      id={buttonId}
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      type="button"
    >
      {content}
    </button>
  );
};

export default Button;